import type { MusclePriorities, MusclePriority } from '@cadencia/contracts';
import { classifyChange, type ChangeClassification } from '../decisions/change-policy.js';
import { MUSCLES, type Muscle } from '../catalog/muscles.js';
import { bandFor, type VolumeBand } from './volume.js';

/**
 * Prioridades musculares (docs/TRAINING_ENGINE.md §4.4).
 *
 * Una prioridad no cambia lo que es seguro: mueve el OBJETIVO dentro de la
 * banda, nunca sus extremos. Marcar un musculo como prioritario no puede sacarlo
 * de su rango util, solo situarlo en la parte alta de ese rango.
 */

/**
 * Los dos tipos se definen en el contrato porque se persisten en la columna
 * `muscle_priorities`. Se reexportan aqui para que el dominio siga siendo el
 * sitio natural donde buscarlos.
 *
 * Lo no declarado es prioridad normal: no hace falta enumerar 18 musculos.
 */
export type { MusclePriorities, MusclePriority };

/**
 * Cuantos musculos pueden llevar prioridad alta a la vez.
 *
 * CONVENCION declarada, no esta en el documento. Priorizarlo todo es no
 * priorizar nada, y ademas es una promesa que el sistema no puede cumplir: el
 * volumen semanal no puede subir mas de un 20 % (SAFETY_LIMITS.volume), asi que
 * repartir ese margen entre ocho musculos no desplaza ninguno de forma util.
 */
export const MAX_HIGH_PRIORITIES = 2;

export function priorityFor(muscle: Muscle, priorities: MusclePriorities): MusclePriority {
  // Se comprueba el valor en lugar de confiar en el tipo. `applyRemoteRow`
  // guarda las filas que llegan del servidor SIN pasarlas por el contrato, asi
  // que un valor corrupto de otro dispositivo llegaria hasta aqui y haria
  // reventar `applyPriority`. Degradar a `normal` es lo seguro: significa "sin
  // enfasis", nunca mas volumen del previsto.
  const valor: unknown = priorities[muscle];
  return valor === 'high' || valor === 'low' || valor === 'normal' ? valor : 'normal';
}

/**
 * Desplaza el objetivo de la banda segun la prioridad.
 *
 * `high` lleva el objetivo al tramo alto (del objetivo actual al maximo) y
 * `low` al tramo bajo (del minimo al objetivo actual). `min` y `max` no se
 * tocan: son el rango util del musculo y no dependen de lo que se quiera
 * enfatizar este bloque.
 */
export function applyPriority(band: VolumeBand, priority: MusclePriority): VolumeBand {
  switch (priority) {
    case 'high':
      return { ...band, targetMin: band.targetMax, targetMax: band.max };
    case 'low':
      return { ...band, targetMin: band.min, targetMax: band.targetMin };
    case 'normal':
      return band;
    default: {
      const exhaustive: never = priority;
      throw new Error(`Prioridad no soportada: ${String(exhaustive)}`);
    }
  }
}

/** Bandas de todos los musculos con las prioridades ya aplicadas. */
export function prioritizedBands(
  priorities: MusclePriorities,
  overrides: Partial<Record<Muscle, VolumeBand>> = {},
): Record<Muscle, VolumeBand> {
  const salida = {} as Record<Muscle, VolumeBand>;
  for (const muscle of MUSCLES) {
    salida[muscle] = applyPriority(bandFor(muscle, overrides), priorityFor(muscle, priorities));
  }
  return salida;
}

const ORDEN: Record<MusclePriority, number> = { high: 0, normal: 1, low: 2 };

/**
 * Ordena para que lo prioritario vaya primero en la sesion (§4.4).
 *
 * Es estable: dentro de un mismo nivel de prioridad se conserva el orden que
 * trae la lista, que es el que decidio el generador por criterios de fatiga y
 * demanda tecnica. Reordenar ahi seria pisar una decision ya tomada.
 */
export function orderByPriority<T>(
  items: readonly T[],
  muscleOf: (item: T) => Muscle,
  priorities: MusclePriorities,
): T[] {
  return [...items].sort(
    (a, b) => ORDEN[priorityFor(muscleOf(a), priorities)] - ORDEN[priorityFor(muscleOf(b), priorities)],
  );
}

export interface PriorityProblem {
  code: 'too_many_high';
  message: string;
}

/**
 * Comprueba que el conjunto de prioridades es realizable.
 *
 * Devuelve los problemas en lugar de lanzar: quien llama decide si avisa al
 * usuario o corrige. Una excepcion aqui obligaria a la UI a envolver en
 * try/catch algo que es una advertencia, no un fallo.
 */
export function validatePriorities(priorities: MusclePriorities): PriorityProblem[] {
  const altas = MUSCLES.filter((muscle) => priorityFor(muscle, priorities) === 'high');
  if (altas.length <= MAX_HIGH_PRIORITIES) return [];
  return [
    {
      code: 'too_many_high',
      message:
        `Hay ${altas.length} músculos con prioridad alta y el máximo útil es ${MAX_HIGH_PRIORITIES}. ` +
        'Con más, el margen de volumen se reparte y ninguno avanza de verdad.',
    },
  ];
}

export interface PriorityDiff {
  muscle: Muscle;
  from: MusclePriority;
  to: MusclePriority;
}

export function diffPriorities(
  before: MusclePriorities,
  after: MusclePriorities,
): PriorityDiff[] {
  const cambios: PriorityDiff[] = [];
  for (const muscle of MUSCLES) {
    const from = priorityFor(muscle, before);
    const to = priorityFor(muscle, after);
    if (from !== to) cambios.push({ muscle, from, to });
  }
  return cambios;
}

export interface PriorityChange {
  changes: PriorityDiff[];
  /** `null` cuando no cambia nada: no hay nada que aprobar. */
  classification: ChangeClassification | null;
}

/**
 * Cambiar prioridades es SIEMPRE cambio relevante (§4.4), asi que pide
 * aprobacion. Guardar las mismas prioridades no es un cambio y no pide nada:
 * una pantalla que pregunta cuando no ha pasado nada enseña a decir que si sin
 * leer, y entonces la aprobacion deja de proteger.
 */
export function classifyPriorityChange(
  before: MusclePriorities,
  after: MusclePriorities,
): PriorityChange {
  const changes = diffPriorities(before, after);
  if (changes.length === 0) return { changes, classification: null };
  return { changes, classification: classifyChange({ kind: 'priority_change' }) };
}
